"use client";

import { ArrowRight, Instagram, Mail, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i} className="h-4 w-32 bg-muted/40 animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-mono text-sm text-muted-foreground hover:text-primary transition-colors duration-300">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const connectLinks = [{
  label: "The Sanctuary",
  to: "/SanctuaryPage"
}, {
  label: "The Vault",
  to: "/VaultPage"
}, {
  label: "Access Rituals",
  to: "/AccessRitualsPage"
}, {
  label: "VR Experience",
  to: "/VRExperiencePage"
}];
const legalLinks = [{
  label: "Privacy Protocol",
  to: "#"
}, {
  label: "Terms of Initiation",
  to: "#"
}, {
  label: "Transmission Rights",
  to: "#"
}, {
  label: "Accessibility",
  to: "#"
}];
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-primary/30 pt-6">
        <p className="font-default text-sm text-foreground/80">{successMessage}</p>
        <Button onClick={resetForm} variant="link" className="px-0 mt-2 font-mono text-xs uppercase tracking-widest text-primary">
          Re-Initiate
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-primary/70">
              Initiate Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="X-01" className="h-10 px-0 border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 font-mono text-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-primary/70">
              Digital Address
            </FormLabel>
            <div className="flex items-end gap-3">
              <FormControl>
                <Input {...field} type="email" placeholder="signal@void.com" className="h-10 px-0 border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 font-mono text-sm" />
              </FormControl>
              <Button type="submit" size="icon" disabled={isSubmitting} variant="ghost" className="h-10 w-10 shrink-0 border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground rounded-md" aria-label="Submit newsletter signup">
                <ArrowRight className="size-4" />
              </Button>
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive updates"
    }} render={({
      field
    }) => <FormItem>
            <label className="flex items-start gap-3 cursor-pointer">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} className="mt-1 size-4 accent-primary bg-transparent border border-border cursor-pointer" />
              </FormControl>
              <span className="font-default text-xs text-muted-foreground leading-relaxed">
                I consent to the transmission of event alerts and exclusive access codes. Unsubscribe at any time.
              </span>
            </label>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 px-4 py-3">
          <p className="font-default text-sm text-destructive">{submitError}</p>
        </div>}
    </Form>;
}
export default function Footer() {
  return <section data-section-id="4892" id="footer" className="bg-background text-foreground border-t border-primary/30 pt-40 pb-12 px-6 md:px-12 lg:px-16 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="mb-20 md:mb-28">
          <h2 className="font-default font-extrabold leading-[0.9] tracking-tighter text-foreground text-[3.5rem] sm:text-[5rem] md:text-[6.5rem] lg:text-[8rem] uppercase">
            Enter the Void<span className="text-primary">.</span>
          </h2>
        </div>

        {}
        <div className="h-px w-full bg-border mb-16 md:mb-20" />

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {}
          <div className="space-y-6 lg:pr-6">
            <Link to="/" aria-label="Church of Over Indulgence — Home">
              <WvcLogo className="h-12 w-auto" />
            </Link>
            <p className="font-default text-sm text-muted-foreground leading-relaxed max-w-xs">
              Immersive event archives · Virtual Reality Sanctuary · Est. 2024
            </p>
            <address className="font-mono text-xs uppercase tracking-wider text-muted-foreground/50 not-italic leading-relaxed">
              0101 Obsidian Plaza
              <br />
              Neo-Tokyo District
              <br />
              Digital Sector 7
            </address>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-primary/70">The Index</h3>
            <MenuProvider menu_id="25" className="footer-explore-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-primary/70">Connect</h3>
            <ul className="space-y-3">
              {connectLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="font-mono text-sm text-muted-foreground hover:text-primary transition-colors duration-300">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-primary/70">Join the Ritual</h3>
            <p className="font-default text-sm text-muted-foreground leading-relaxed">
              Receive transmission alerts for live streams and VR drops.
            </p>
            <FormProvider formKey="newsletter_signup_sanctuary" formId="newsletter_signup_sanctuary" sectionName="footer" defaultValues={{
            first_name: "",
            email: "",
            consent: false
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            },
            consent: {
              required: true
            }
          }} submitText="Initiate" successMessage="Transmission established. Welcome to the Church." errorMessage="Signal lost. Please re-attempt initiation.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        {}
        <div className="h-px w-full bg-border mt-24 mb-10" />

        {}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          {}
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.15em] text-muted-foreground/40 order-2 lg:order-1">
            © 2024 Church of Over Indulgence · N° 001 · All Rights Reserved
          </p>

          {}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 order-3 lg:order-2">
            {legalLinks.map((link, i) => <div key={link.label} data-index={i} className="flex items-center gap-3">
                <Link to={link.to} className="font-mono text-[0.7rem] uppercase tracking-[0.15em] text-muted-foreground/50 hover:text-primary transition-colors duration-300">
                  {link.label}
                </Link>
                {i < legalLinks.length - 1 && <span className="font-mono text-[0.7rem] text-muted-foreground/30">·</span>}
              </div>)}
          </div>

          {}
          <div className="flex items-center gap-2 order-1 lg:order-3">
            <Link to="#" newTab aria-label="Instagram" className="size-10 border border-border hover:border-primary hover:text-primary text-muted-foreground flex items-center justify-center transition-colors duration-300 rounded-md">
              <Instagram className="size-4" strokeWidth={1.25} />
            </Link>
            <Link to="#" newTab aria-label="Vimeo" className="size-10 border border-border hover:border-primary hover:text-primary text-muted-foreground flex items-center justify-center transition-colors duration-300 rounded-md">
              <Video className="size-4" strokeWidth={1.25} />
            </Link>
            <Link to="#" newTab aria-label="Journal newsletter" className="size-10 border border-border hover:border-primary hover:text-primary text-muted-foreground flex items-center justify-center transition-colors duration-300 rounded-md">
              <Mail className="size-4" strokeWidth={1.25} />
            </Link>
          </div>
        </div>

        {}
        <div className="mt-16 flex justify-end">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-primary/50">
            — Indulge deliberately. Witness everything.
          </p>
        </div>
      </div>
    </section>;
}